import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';
import { useTranslation } from 'react-i18next';

import { OpSpace } from 'new-components/DLS/OpSpace/OpSpace';
import { OpTable } from 'new-components/DLS/OpTable/OpTable';
import { ApiEditButton } from 'new-components/DLS/OpTableCore/components/ApiEditButton';
import { tableStateKeys } from 'new-components/DLS/OpTableCore/constants/tableStateKeys';
import {
  OpTableRawColumnType,
  OpTableRecordType,
} from 'new-components/DLS/OpTableCore/types';
import { useSelectorJs } from 'utils/customHooks';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive } from 'utils/helpers';
import { useRemoveQueryParams } from 'utils/customHooks/useRemoveQueryParams';
import { useAddQueryParams } from 'utils/customHooks/useAddQueryParams';
import { useUrlSearchParams } from 'utils/customHooks/useUrlSearchParams';

import DateRangeLocationFilter2 from '../../../components/filters/DateRangeLocationFilter2';
import { useVisitsUrlFilter } from '../../../hooks/useVisitsUrlFilter';
import { DATE_TIME_AM_PM_FORMAT } from '../../../constants/dates';
import { TABLE_HEIGHT } from '../../../constants/ui';
import { GuestPassState } from '../../../store/slices/guestPassSlice';
import {
  fetchLocations,
  LocationsState,
} from '../../../store/slices/locationsSlice';
import {
  fetchVisits,
  setSelectedVisit,
  setVisitsEndDate,
  setVisitsStartDate,
} from '../../../store/slices/visitsSlice';
import { AppDispatch, ImmutableRootState } from '../../../store/store';
import { GuestPass } from '../../../types/guestPassTypes';
import { Location } from '../../../types/locationTypes';
import { Visit, VisitStatus } from '../../../types/visitTypes';
import { formatFullName, statusKeyMap } from '../../../utils/utils';
import { getStatusNameById } from '../../../utils/visitorsHelper';
import VisitorsDrawer from '../../visitors/drawer/VisitorsDrawer';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

interface GuestPassReportTableData {
  id: number;
  visitId: number;
  visitor: string;
  status: string;
  validFrom: string;
  validTo: string;
}

const GuestPassReportTable = () => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { userId } = useIdentityAndUser();
  const { isValid: hasAllvisitorsRead } = useValidateScopes([
    'o{orgId}-visitorAllVisitors:r',
    'o{orgId}-visitorVisitors:r',
    'o:r',
    's-o:r',
  ]);
  const { isValid: hasGuestpassRead } = useValidateScopes([
    'o{orgId}-visitorGuestPass:r',
    'o{orgId}-visitorRpt:r',
    'o{orgId}-visitorRptGuestPass:r',
    'o:r',
    's-o:r',
  ]);

  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  ) as LocationsState['locations'];
  const triggerUpdate = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['guestPass', 'triggerUpdate']),
  ) as GuestPassState['triggerUpdate'];

  const [guestPasses, setGuestPasses] = useState<GuestPass[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // URL-driven location + date filter (single source of truth). Defaults to the
  // current week.
  const {
    urlLocationId,
    urlStartDate,
    urlEndDate,
    locationId: queryLocationId,
    startDate: queryStartDate,
    endDate: queryEndDate,
    startDateOnly,
    endDateOnly,
    isReady: filterReady,
    setFilter,
  } = useVisitsUrlFilter({ defaultRange: 'week' });
  const [visitStatus, setVisitStatus] = useState<VisitStatus[]>([]);
  const [statusFilterOptions, setStatusFilterOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [defaultStatusFilter, setDefaultStatusFilter] = useState<string[]>([]);

  const [isVisitorsDrawerOpen, setIsVisitorsDrawerOpen] = useState(false);
  const { drawer, visitId: visitIdString } = useUrlSearchParams();
  const removeQueryParams = useRemoveQueryParams();
  const addQueryParams = useAddQueryParams();

  // Memoized fetchGuestPasses function
  const fetchGuestPasses = useCallback(async () => {
    setLoading(true);
    try {
      const guestPassResponseRaw = await heliumReceive(
        'getMsiAltaGuestPassByDate',
        [orgId],
        {
          queryStringParams: {
            startDate: queryStartDate,
            endDate: queryEndDate,
            status: 1,
          },
        },
      );
      const guestPassResponse = guestPassResponseRaw as {
        data?: GuestPass[] | null;
      };

      // Fetch locations and extract .data
      if (orgId && locations.length === 0) {
        dispatch(fetchLocations({ orgId, status: 1 }));
      }

      const filteredGuestPasses = (guestPassResponse.data ?? []).filter(
        (guestPass: GuestPass) => {
          const matchesLocation =
            (queryLocationId === -1 ||
              guestPass.locationId === queryLocationId) &&
            locations.some(
              (location: Location) => location.id === guestPass.locationId,
            );

          return (
            matchesLocation &&
            (hasAllvisitorsRead || userId === guestPass.hostUserId) &&
            guestPass.visitStatus !== 0
          );
        },
      );
      setGuestPasses(filteredGuestPasses);
    } catch {
      // Failed to fetch guest passes
    } finally {
      setLoading(false);
    }
  }, [
    dispatch,
    locations,
    orgId,
    queryStartDate,
    queryEndDate,
    queryLocationId,
    hasAllvisitorsRead,
    userId,
  ]);

  // Fetch visits from the URL-driven filter (race-proof). Keeps Redux dates in
  // sync so fetchVisits({ orgId }) callers stay consistent.
  useEffect(() => {
    if (
      !filterReady ||
      queryLocationId == null ||
      queryStartDate == null ||
      queryEndDate == null
    ) {
      return;
    }
    dispatch(setVisitsStartDate(queryStartDate));
    dispatch(setVisitsEndDate(queryEndDate));
    dispatch(
      fetchVisits({
        orgId,
        locationId: queryLocationId,
        startDate: queryStartDate,
        endDate: queryEndDate,
      }),
    );
    // eslint-disable-next-line
  }, [orgId, filterReady, queryLocationId, queryStartDate, queryEndDate]);

  useEffect(() => {
    fetchGuestPasses();
  }, [fetchGuestPasses, triggerUpdate]);

  useEffect(() => {
    const fetchVisitStatuses = async () => {
      try {
        const visitorStatus = await heliumReceive('getVisitorStatus', [orgId]);
        setVisitStatus(visitorStatus?.data || []);
      } catch {
        // Failed to fetch visitor statuses
      }
    };

    if (orgId) {
      fetchVisitStatuses();
    }
  }, [orgId]);

  useEffect(() => {
    if (visitStatus.length > 0) {
      const options = [
        ...visitStatus.map((status) => ({
          label: t(status.languageCode ?? '', status.name),
          value: status.name,
        })),
        {
          label: t('vst_data_noshow', 'No Show'),
          value: 'No Show',
        },
      ];

      setStatusFilterOptions(options);
      setDefaultStatusFilter(options.map((option) => option.value));
    }
  }, [visitStatus, t]);

  // Table columns definition
  const columns: OpTableRawColumnType[] = [
    {
      dataIndex: 'visitor',
      label: t('guestPassReport.table.visitor'),
      filter: { type: 'input' },
      sorter: (a, b) => (a.visitor || '').localeCompare(b.visitor || ''),
    },
    {
      dataIndex: 'status',
      label: t('guestPassReport.table.status'),
      filter: {
        type: 'multiSelect',
        options: statusFilterOptions,
      },

      defaultFilteredValue: defaultStatusFilter,
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
      onFilter: (value, record) => record.status === value,
      render: (value) => t(statusKeyMap[value] || value),
    },
    {
      dataIndex: 'passStatus',
      label: t('guestPassReport.table.passStatus'),
      filter: {
        type: 'multiSelect',
        options: [
          { label: t('guestPassReport.table.valid'), value: 'Valid' },
          { label: t('guestPassReport.table.invalid'), value: 'Invalid' },
        ],
      },
      defaultFilteredValue: ['Valid', 'Invalid'],
      sorter: (a, b) => a.passStatus.localeCompare(b.passStatus),
      onFilter: (value, record) => {
        return record.passStatus === value;
      },
      render: (value) => {
        const color = value === 'Valid' ? '#709A7A' : 'grey';
        const textColor = 'white';
        const translated =
          value === 'Valid'
            ? t('guestPassReport.table.valid')
            : t('guestPassReport.table.invalid');

        return (
          <div
            style={{
              backgroundColor: color,
              color: textColor,
              padding: '5px 10px',
              borderRadius: '4px',
              textAlign: 'center',
            }}
          >
            {translated}
          </div>
        );
      },
    },
    {
      dataIndex: 'validFrom',
      label: t('guestPassReport.table.validFrom'),
      sorter: (a, b) => dayjs(b.dateTime).diff(a.dateTime),
    },
    {
      dataIndex: 'validTo',
      label: t('guestPassReport.table.validTo'),
      sorter: (a, b) => dayjs(b.dateTime).diff(a.dateTime),
    },
    {
      dataIndex: 'createdAt',
      label: t('guestPassReport.table.createdAt'),
      sorter: (a, b) => dayjs(b.dateTime).diff(a.dateTime),
      hidden: true,
    },
    {
      dataIndex: 'updatedAt',
      label: t('guestPassReport.table.updatedAt'),
      sorter: (a, b) => dayjs(b.dateTime).diff(a.dateTime),
      hidden: true,
    },
    {
      dataIndex: 'location',
      label: t('guestPassReport.table.location'),
      filter: {
        type: 'multiSelect',
        options: locations.map((location) => ({
          label: location.name,
          value: location.id,
        })),
      },
      defaultFilteredValue: locations.map((location) => String(location.id)),
      sorter: (a, b) => a.location.localeCompare(b.location),
      onFilter: (value, record) => {
        return String(record.locationId) === String(value);
      },
      hidden: true,
    },
    {
      dataIndex: 'issuer',
      label: t('guestPassReport.table.issuer'),
      filter: { type: 'input' },
      sorter: (a, b) => (a.issuer || '').localeCompare(b.issuer || ''),
      hidden: true,
    },
  ];

  const handleDateRangeLocationFilter = (
    locationId: number,
    nextStartDate: string,
    nextEndDate: string,
  ) => {
    setFilter(locationId, nextStartDate, nextEndDate);
  };

  const getPassStatus = (startUtc: string, endUtc: string): string => {
    const now = dayjs();
    return now.isBetween(
      dayjs(startUtc).utc(true),
      dayjs(endUtc).utc(true),
      null,
      '[]',
    )
      ? 'Valid'
      : 'Invalid';
  };

  // Format activities for table display
  const formatGuestPassesReportForTable = (
    guestPassList: GuestPass[],
  ): GuestPassReportTableData[] => {
    return guestPassList.map((guestPass) => {
      const foundLocation = locations.find(
        (location) => location.id === guestPass.locationId,
      );
      const generatedBy = guestPass.generatedBy?.[0];
      const issuerName = generatedBy
        ? formatFullName(generatedBy.firstName, null, generatedBy.lastName)
        : '';

      return {
        id: guestPass.id,
        visitId: guestPass.visitId,
        visitor:
          formatFullName(
            guestPass.visitor[0]?.firstName,
            guestPass.visitor[0]?.middleName,
            guestPass.visitor[0]?.lastName,
          ) +
          (guestPass.visitor.length > 1
            ? ` (+${guestPass.visitor.length - 1})`
            : ''),
        status: getStatusNameById(guestPass.visitStatus, undefined),
        validFrom: dayjs(guestPass.startUtc)
          .utc(true)
          .local()
          .format(DATE_TIME_AM_PM_FORMAT),
        validTo: dayjs(guestPass.endUtc)
          .utc(true)
          .local()
          .format(DATE_TIME_AM_PM_FORMAT),
        passStatus: getPassStatus(guestPass.startUtc, guestPass.endUtc),
        location: foundLocation?.name,
        createdAt: dayjs(guestPass.createdAt)
          .utc(true)
          .local()
          .format(DATE_TIME_AM_PM_FORMAT),
        updatedAt: dayjs(guestPass.updatedAt)
          .utc(true)
          .local()
          .format(DATE_TIME_AM_PM_FORMAT),
        locationId: guestPass.locationId,
        issuer: issuerName,
      };
    });
  };

  // Loads the visit for the drawer into Redux from the Helium API and opens it.
  const openVisitDrawer = useCallback(
    async (visitId: number) => {
      const responseRaw = await heliumReceive('describeVisit', [
        orgId,
        visitId,
      ]);
      const response = responseRaw as { data?: Visit[] | null };
      const selectedVisit = response?.data?.[0] as Visit | undefined;

      if (selectedVisit) {
        dispatch(setSelectedVisit(selectedVisit));
        setIsVisitorsDrawerOpen(true);
      }
    },
    [orgId, dispatch],
  );

  const handleVisitClick = (visit: OpTableRecordType) => {
    const typedVisit = visit as GuestPassReportTableData;
    addQueryParams({
      drawer: 'edit',
      visitId: String(typedVisit.visitId),
    });
  };

  // Restore the visitor drawer from the URL (e.g. direct link or refresh) by
  // loading the visit from the visitId query param.
  useEffect(() => {
    if (drawer === 'edit' && visitIdString && orgId) {
      openVisitDrawer(Number(visitIdString));
    }
  }, [drawer, visitIdString, orgId, openVisitDrawer]);

  const handleDrawerClose = () => {
    setIsVisitorsDrawerOpen(false);
    dispatch(setSelectedVisit(null));
    removeQueryParams(['drawer', 'visitId']);
  };

  // Table properties
  const opTableProps = {
    uiStateKey: tableStateKeys.VisitorGuestPassReportMainTable,
    dataSource: formatGuestPassesReportForTable(guestPasses),
    label: t('guestPassReport.table.label'),
    columns,
    onRowClick: (record: OpTableRecordType) => handleVisitClick(record),
    rowActions: (record: OpTableRecordType) => [
      <ApiEditButton
        key="view"
        hasWritePermission={false}
        onClick={() => handleVisitClick(record)}
      />,
    ],
    height: TABLE_HEIGHT,
    allowGlobalSearch: true,
    allowExport: true,
    allowShowHideColumns: true,
    loading,
    gtm: 'activity-log-table-gtm',
    rowKey: 'id',
  };

  if (statusFilterOptions.length === 0 || defaultStatusFilter.length === 0) {
    return null;
  }

  return (
    <OpSpace orientation="vertical" size="middle" style={{ display: 'flex' }}>
      <DateRangeLocationFilter2
        key={`${urlLocationId ?? ''}-${urlStartDate ?? ''}-${urlEndDate ?? ''}`}
        onDateRangeLocationFilter={handleDateRangeLocationFilter}
        initialStartDate={startDateOnly}
        initialEndDate={endDateOnly}
      />

      <OpTable {...opTableProps} />

      {isVisitorsDrawerOpen && (
        <VisitorsDrawer
          open={isVisitorsDrawerOpen}
          onClose={handleDrawerClose}
          tab={hasGuestpassRead ? 'guestPass' : 'visitors'}
        />
      )}
    </OpSpace>
  );
};

export default GuestPassReportTable;
